/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTvrexCertificates, login } from "./multiplayer/SocketConnection";
import { setCanModify } from "./store/slice/privateSlice";
import { initializeUser } from "./store/slice/userSlice";
import { updateRoomData } from "./store/slice/roomSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getConfiguredStunServer, getFerrari, getIsInMaintenance, getIsRoomAlive } from "./controllers/internalApi";
import { CONTRIBUTOR } from "./store/constants/sharedModeFields";
import Maintenance from "./utils/Maintenance";
import * as webRTCHandler from "./handlers/webRTCHandler"

// PATRONUS
import { Heading } from "@patron/patron-react/heading"
import { TextInput } from "@patron/patron-react/textinput"
import { Button } from "@patron/patron-react/button"
import { Label } from "@patron/patron-react/label"
import { Paragraph } from "@patron/patron-react/paragraph"
import { Password } from "@patron/patron-react/password"
import { FormHelperText } from "@patron/patron-react/formhelpertext"

import '@patron/patron-css/patron/index.css';
import "./Login.scss"

const PREFIX = "app"

export default function Login() {
    const params = useParams();
    const location = useLocation();
    const audioOnly = useSelector((state) => state.room.audioOnly);
    const configuredStunServer = useSelector((state) => state.room.configuredStunServer);
    const isInMaintenance = useSelector((state) => state.room.isInMaintenance);
    const isRoomAlive = useSelector((state) => state.room.isRoomAlive);
    const ferrari = useSelector((state) => state.private.ferrari);

    const [username, setUserName] = useState()
    const [password, setPassword] = useState()
    const [isInputWrong, setIsInputWrong] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const inCooperative = location.pathname.includes("cooperative");
    const userAgent = window.navigator.userAgent
    const data = inCooperative ? window.atob(params.data).split("&&") : null;
    const roomId = inCooperative ? data[0] : null;
    const role = inCooperative ? data[1] : null;

    useEffect(() => {
        const html = document.getElementsByTagName("html")[0];
        if (html) html.classList.remove("a-fullscreen");

        if (isInMaintenance === "checking") getIsInMaintenance()

        if (inCooperative) getIsRoomAlive(roomId);

        if (!configuredStunServer) getConfiguredStunServer();

        if (!ferrari) getFerrari();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    let loggingUserData = { username, password, inCooperative, roomId, userAgent }

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const pressEnter = (e) => {
        if (e.key.toLowerCase() === "enter") {
            document.getElementById("login-vr").click();
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        setIsLoading(true);
        if (username && password) {
            if (inCooperative)
                if (configuredStunServer)
                    webRTCHandler.getLocalStreamPreview(audioOnly, () => {
                        login({ ...loggingUserData, role: role }, loginResponseCallback);
                    });
                else
                    login({ ...loggingUserData, role: role }, loginResponseCallback);
            else
                login(loggingUserData, loginResponseCallback);
        } else {
            setIsInputWrong("Please insert credentials.");
            setIsLoading(false);
        }
    }

    const loginResponseCallback = (res, socketId) => {
        if (res.flag) {
            if (inCooperative) {
                dispatch(setCanModify(role === CONTRIBUTOR))
                dispatch(updateRoomData(res.room))
            } else getTvrexCertificates();

            dispatch(initializeUser({ username, socketId }));
            navigate(inCooperative ? "/cooperative/dashboard" : "/dashboard");
        } else
            setIsInputWrong(
                res.reason === "ECONNECT"
                    ? "Cannot connect to DWC."
                    : "Wrong username or password.",
            );

        setIsLoading(false);
    }

    return isInMaintenance === "checking" ? null
        : isInMaintenance ? <Maintenance /> : (
            <>
                <section>
                    <div className="container">
                        <div style={{ marginTop: "5%" }}>
                            <Heading
                                type="h3"
                                className={`hcl-text-center mb-5 ${PREFIX}-login-title`}
                            >
                                HCL T-VREX
                            </Heading>
                        </div>
                    </div>
                    <div className="container">
                        <div className="blue_active_blue_dark">
                            <div className={`${PREFIX}-login-wrapper`}>
                                <div className="hcl-text-center mb-4">
                                    <img
                                        style={{ width: "6.5rem", margin: "2%" }}
                                        src="/assets/tvrex.png"
                                        alt="TVREX"
                                    />

                                    {
                                        inCooperative && !isRoomAlive ? <Paragraph style={{
                                            fontSize: "15px",
                                            marginTop: "8%"
                                        }} >
                                            The followed link has expired.
                                        </Paragraph> : <Paragraph className="hcl-type-legal mt-1">
                                            Use your DWC credentials to login
                                        </Paragraph>
                                    }
                                </div>
                                <form className="p-5">
                                    {isRoomAlive || !inCooperative ?
                                        <><div className="hcl-form-group">
                                            <TextInput
                                                className="custom-dark-input"
                                                placeholder="Username"
                                                id="username"
                                                value={username}
                                                onKeyUp={pressEnter}
                                                onChange={(e) => {
                                                    setUserName(e.target.value);
                                                    setIsInputWrong(null);
                                                }}
                                                data-invalid={isInputWrong}
                                                type="text"
                                                required />
                                            <Label htmlFor="username">Username</Label>
                                        </div><div className="hcl-form-group">
                                                <Password
                                                    className="custom-dark-input"
                                                    placeholder="Password"
                                                    id="password"
                                                    value={password}
                                                    onKeyUp={pressEnter}
                                                    onChange={(e) => {
                                                        setPassword(e.target.value);
                                                        setIsInputWrong(null);
                                                    }}
                                                    data-invalid={isInputWrong}
                                                    required />
                                                <Label htmlFor="password">Password</Label>
                                            </div><div className="hcl-text-center">
                                                <FormHelperText className="error-msg">
                                                    {isInputWrong}
                                                </FormHelperText>
                                                <Button
                                                    className={`hcl-primary`}
                                                    id="login-vr"
                                                    type="primary"
                                                    onClick={handleLogin}
                                                >
                                                    {isLoading ? "Loading..." : "Login"}
                                                </Button>
                                            </div></> : null
                                    }
                                    <Paragraph className="hcl-type-legal mt-4">
                                        &copy; Copyright HCL Technologies Limited 2019. All Rights
                                        Reserved.
                                    </Paragraph>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </>
        )
}